// Closed type sets for the operational and finance contexts [03 §6–§16, §19.1 `e(...)` columns;
// BUILD-PLAN §9; 04 §16; 06 §16]. Statuses with transitions live in ./machines-*.ts.
import { defineEnum } from './define.ts';
import type { EnumValue } from './define.ts';

// ---- materials ----
export const FABRIC_CUT_ORIENTATION = defineEnum('materials.fabric.cut_orientation', [
  'one_way',
  'two_way',
  'across',
]);
export const FABRIC_CONSTRUCTION = defineEnum('materials.fabric.construction', [
  'knit',
  'woven',
  'other',
]);
export const FABRIC_PRICE_SOURCE = defineEnum('materials.fabric_price.source', [
  'quote',
  'po',
  'manual',
  'migration',
]);
export const TRIM_TYPE = defineEnum('materials.trim.trim_type', [
  'button',
  'zipper',
  'label',
  'elastic',
  'thread',
  'interlining',
  'packaging',
  'hanger',
  'other',
]);
export const LOT_TEST_STATUS = defineEnum('materials.lot.test_status', [
  'not_required',
  'pending',
  'passed',
  'failed',
  'waived',
]);
export const FABRIC_TEST_STAGE = defineEnum('materials.fabric_test.stage', [
  'development',
  'pre_production',
  'bulk',
  'retest',
  'customer',
]);
export const FABRIC_TEST_RESULT = defineEnum('materials.fabric_test.result', [
  'pending',
  'pass',
  'fail',
  'conditional',
]);
export const QUALITY_HOLD_SCOPE = defineEnum('materials.quality_hold.scope', [
  'lot',
  'roll',
  'item_warehouse',
]);

// ---- product ----
export const BASE_STYLE_CONSTRUCTION = defineEnum('product.base_style.construction', [
  'knitted',
  'woven',
  'other',
]);
export const STYLE_COLOURWAY_STATUS = defineEnum('product.style_colourway.status', [
  'draft',
  'active',
  'dropped',
]);
export const STYLE_PRICE_SOURCE = defineEnum('product.style_price.source', [
  'cost_sheet',
  'manual',
  'import',
]);
export const BOM_LINE_TYPE = defineEnum('product.bom_line.line_type', ['fabric', 'trim']);
export const SAMPLE_PURPOSE = defineEnum('product.sample.purpose', [
  'proto',
  'fit',
  'size_set',
  'pp',
  'top',
  'salesman',
  'customer_show',
  'photoshoot',
  'testing',
]);
export const SAMPLE_EVENT = defineEnum('product.sample_event.event', [
  'status',
  'moved',
  'sent',
  'received',
  'photographed',
  'comment',
]);
export const DEV_APPROVAL_SUBJECT = defineEnum('product.dev_approval.subject_type', [
  'fit_sample',
  'strike_off',
  'lab_dip',
  'fabric_test',
  'trim',
  'spec',
  'bom',
  'artwork',
  'pp_sample',
  'cad',
]);
/** G9 mode, setting `development.readiness_gate_mode` [04 §3, BR-DEV-004]. */
export const READINESS_GATE_MODE = defineEnum('development.readiness_gate_mode', [
  'off',
  'warn',
  'block',
]);
export type ReadinessGateMode = EnumValue<typeof READINESS_GATE_MODE>;
/** Stages a development requirement can block [04 §9 BR-DEV-003]; stored on dev_requirement (CR-W2a-39, Q-DEV-01). */
export const READINESS_STAGE = defineEnum('product.dev_requirement.stage', [
  'order_confirm',
  'cut_release',
  'dispatch',
]);
export type ReadinessStage = EnumValue<typeof READINESS_STAGE>;

// ---- costing ----
export const COST_SHEET_PURPOSE = defineEnum('costing.cost_sheet.purpose', ['estimate', 'quote']);
export const COST_LINE_TYPE = defineEnum('costing.cost_line.line_type', [
  'fabric',
  'trim',
  'labour',
  'packaging',
  'testing',
  'freight',
  'duty',
  'overhead',
  'other',
]);
export const COST_PRICE_SOURCE = defineEnum('costing.cost_line.price_source', [
  'material_price',
  'contractor_rate',
  'setting',
  'manual',
]);
export const ACTUAL_COST_SOURCE = defineEnum('costing.actual_cost_line.source_type', [
  'stock_movement',
  'ap_document_line',
  'landed_cost',
  'contractor_rate',
  'manual',
]);
export const WEEKLY_CLOSE_STATUS = defineEnum('costing.weekly_close.status', [
  'open',
  'closed',
  'reopened',
]);
export const LANDED_CHARGE_TYPE = defineEnum('costing.landed_cost_charge.charge_type', [
  'freight',
  'duty',
  'broker',
  'insurance',
  'handling',
  'other',
]);
export const LANDED_CHARGE_BASIS = defineEnum('costing.landed_cost_charge.basis', [
  'qty',
  'value',
  'weight',
  'manual',
]);
export const CUSTOMER_CONTRIBUTION_COST_TYPE = defineEnum(
  'costing.customer_contribution_cost.cost_type',
  [
    'presenter',
    'outbound_freight',
    'markdown',
    'listing_allowance',
    'rebate',
    'return',
    'chargeback',
    'other',
  ],
);

// ---- sales ----
export const PROGRAMME_KIND = defineEnum('sales.programme.kind', [
  'show',
  'reorder',
  'seasonal_lineup',
  'web',
  'sample',
]);
export const PROGRAMME_LINE_STATUS = defineEnum('sales.programme_line.status', [
  'proposed',
  'approved',
  'dropped',
]);
export const PROGRAMME_MILESTONE = defineEnum('sales.programme_milestone.milestone', [
  'po_confirmation',
  'samples_due',
  'fabric_ordered',
  'fabric_tested',
  'bulk_in_house',
  'cut',
  'sewn',
  'photoshoot',
  'ship_by',
  'in_dc',
]);
export const PROGRAMME_MILESTONE_STATUS = defineEnum('sales.programme_milestone.status', [
  'open',
  'done',
  'late',
  'waived',
]);
export const ORDER_TYPE = defineEnum('sales.sales_order.order_type', [
  'new',
  'reorder',
  'sample',
  'replenishment',
]);
export const ORDER_SOURCE = defineEnum('sales.sales_order.source', [
  'manual',
  'po_import',
  'migration',
]);
export const ORDER_LINE_PRICE_SOURCE = defineEnum('sales.sales_order_line.price_source', [
  'price_list',
  'cost_sheet',
  'po_import',
  'manual',
]);
export const ORDER_LINE_STATUS = defineEnum('sales.sales_order_line.line_status', [
  'open',
  'cancelled',
  'closed',
]);
export const ORDER_REVISION_SOURCE = defineEnum('sales.sales_order_revision.source', [
  'manual',
  'po_import',
]);
export const PO_IMPORT_CHANNEL = defineEnum('sales.po_import_document.channel', [
  'email',
  'upload',
  'portal',
]);
export const PO_IMPORT_EXTRACTION = defineEnum('sales.po_import_document.extraction_method', [
  'deterministic',
  'llm_fallback',
]);
export const PO_IMPORT_MATCH_STATUS = defineEnum('sales.po_import_line.match_status', [
  'matched',
  'unmatched',
  'ambiguous',
  'ignored',
]);
/** + 06 §16 #7 (ambiguous_style … terms_mismatch). */
export const PO_IMPORT_EXCEPTION_TYPE = defineEnum('sales.po_import_exception.type', [
  'unknown_item',
  'unknown_colour',
  'unknown_size',
  'not_in_lineup',
  'qty_mismatch',
  'price_mismatch',
  'date_mismatch',
  'currency_mismatch',
  'duplicate_po',
  'revision_detected',
  'below_shipped',
  'cancel_after_partial_ship',
  'total_mismatch',
  'low_confidence',
  'parse_error',
  'ambiguous_style',
  'style_not_orderable',
  'line_arithmetic',
  'below_cut',
  'terms_mismatch',
]);
export const PO_IMPORT_EXCEPTION_SEVERITY = defineEnum('sales.po_import_exception.severity', [
  'block',
  'warn',
]);
export const PO_IMPORT_EXCEPTION_STATUS = defineEnum('sales.po_import_exception.status', [
  'open',
  'resolved',
  'waived',
]);
export const PO_IMPORT_EXCEPTION_RESOLUTION = defineEnum('sales.po_import_exception.resolution', [
  'mapped',
  'corrected',
  'accepted',
  'line_rejected',
  'document_rejected',
]);
export const RETURN_DISPOSITION = defineEnum('sales.customer_return_line.disposition', [
  'restock',
  'seconds',
  'scrap',
  'return_to_contractor',
]);

// ---- inventory ----
export const ITEM_TYPE = defineEnum('inventory.item.item_type', ['fabric', 'trim', 'finished']);
export const RESERVATION_DEMAND_TYPE = defineEnum('inventory.stock_reservation.demand_type', [
  'cutting_slip',
  'sales_order_line',
  'shipment',
  'd2c_order',
  'warehouse_document',
]);
export const WAREHOUSE_DOCUMENT_TYPE = defineEnum('inventory.warehouse_document.doc_type', [
  'transfer',
  'physical_count',
  'assembly',
  'disassembly',
  'category_change',
  'd2c_release',
  'd2c_pullback',
  'roll_split',
]);
export const WAREHOUSE_LINE_STATUS = defineEnum('inventory.warehouse_document_line.line_status', [
  'valid',
  'unknown_barcode',
  'error',
  'ignored',
]);
export const ITEM_COST_METHOD = defineEnum('inventory.item_cost.method', [
  'lot_specific',
  'moving_average',
  'actual_job',
]);
export const BARCODE_STATUS = defineEnum('inventory.barcode.status', [
  'active',
  'inactive',
  'merged',
]);
/** Ledger movement types — closed set, one writer each [03 §19.1; MB D7]. */
export const MOVEMENT_TYPE = defineEnum('inventory.stock_movement.movement_type', [
  'opening_balance',
  'po_receipt',
  'receipt_reversal',
  'supplier_return',
  'cut_issue',
  'cut_return',
  'fg_receipt',
  'shipment',
  'customer_return',
  'd2c_sale',
  'd2c_return',
  'transfer_out',
  'transfer_in',
  'category_out',
  'category_in',
  'roll_split_out',
  'roll_split_in',
  'assembly_consume',
  'assembly_produce',
  'count_variance',
  'adjustment',
  'write_off',
  'revaluation',
  // CR-BG2d (W2d.shipping R-LED-SHP-1): BR-SHP-013 reopen reverses a posted `shipment` movement (`reversalOfId`).
  'shipment_reversal',
  // CR-BG2e: a reversed FG receipt takes back (part of) one posted `fg_receipt` movement at its cost (W2e.sewing-fg
  // R-CORE-FGR-1, was a negative `adjustment`); a reversal note puts back (part of) one `supplier_return` (W2e.notes
  // R-LED-CDN-1, was a positive `adjustment`). Both name the reversed movement (`reversalOfId`), like shipment_reversal.
  'fg_receipt_reversal',
  'supplier_return_reversal',
]);
export type MovementType = EnumValue<typeof MOVEMENT_TYPE>;
/** Sign rule per movement type: +1 in, −1 out, 0 either (± by line), 'value' = qty 0 [03 §19.1]. */
export const MOVEMENT_SIGN: Readonly<Record<MovementType, 1 | -1 | 0 | 'value'>> = Object.freeze({
  opening_balance: 1,
  po_receipt: 1,
  receipt_reversal: -1,
  supplier_return: -1,
  cut_issue: -1,
  cut_return: 1,
  fg_receipt: 1,
  shipment: -1,
  customer_return: 1,
  d2c_sale: -1,
  d2c_return: 1,
  transfer_out: -1,
  transfer_in: 1,
  category_out: -1,
  category_in: 1,
  roll_split_out: -1,
  roll_split_in: 1,
  assembly_consume: -1,
  assembly_produce: 1,
  count_variance: 0,
  adjustment: 0,
  write_off: -1,
  revaluation: 'value',
  shipment_reversal: 1,
  fg_receipt_reversal: -1,
  supplier_return_reversal: 1,
});

// ---- purchasing ----
export const PO_TYPE = defineEnum('purchasing.purchase_order.po_type', [
  'fabric',
  'trim',
  'service',
]);
export const PO_LINE_STATUS = defineEnum('purchasing.purchase_order_line.line_status', [
  'open',
  'closed',
  'cancelled',
]);
export const PO_COMMITMENT_DEMAND_TYPE = defineEnum('purchasing.po_line_commitment.demand_type', [
  'cutting_slip',
  'sales_order_line',
  'style',
]);
export const PO_COMMITMENT_STATUS = defineEnum('purchasing.po_line_commitment.status', [
  'open',
  'fulfilled',
  'cancelled',
]);
export const INBOUND_MODE = defineEnum('purchasing.inbound_shipment.mode', [
  'sea',
  'air',
  'road',
  'courier',
]);
export const INBOUND_FEED_SOURCE = defineEnum('purchasing.inbound_shipment.feed_source', [
  'manual',
  'forwarder_api',
  'file_import',
]);
export const ETA_SUBJECT = defineEnum('purchasing.eta_event.subject_type', [
  'po_line',
  'inbound_shipment',
]);
export const ETA_MILESTONE = defineEnum('purchasing.eta_event.milestone', [
  'supplier_ready',
  'departure',
  'port_arrival',
  'customs_release',
  'warehouse_delivery',
  'promised_ship',
  'revised_ship',
]);
export const ETA_KIND = defineEnum('purchasing.eta_event.kind', ['estimate', 'actual']);
export const ETA_SOURCE = defineEnum('purchasing.eta_event.source', [
  'supplier',
  'forwarder_feed',
  'file_import',
  'manual',
]);

// ---- production ----
export const CUT_SHORTFALL_DECISION = defineEnum('production.cut_shortfall.decision', [
  'cut_reduced',
  'substitute',
  'wait_for_po',
  'override',
]);

// ---- dispatch ----
/** + 06 §16 #6. */
export const CUSTOMER_DOCUMENT_TYPE = defineEnum('dispatch.customer_document_profile.doc_type', [
  'commercial_invoice',
  'packing_list',
  'bill_of_lading',
  'container_list',
  'carton_label',
  'hs_declaration',
  'delivery_note',
  'broker_document',
  'air_shipment_document',
  'master_shipping_list',
  'shipment_summary',
  'items_to_ship',
  'master_carton_label',
  'skid_label',
  'po_confirmation',
]);
export const SHIPPING_DOCUMENT_STATUS = defineEnum('dispatch.shipping_document.status', [
  'draft',
  'generated',
  'final',
  'void',
]);
export const LABEL_RUN_TYPE = defineEnum('dispatch.label_run.run_type', [
  'care',
  'bag',
  'hang_tag',
  'barcode',
  'market_sticker',
  'carton',
]);
/** 06 §16 #15. */
export const LABEL_RUN_MODE = defineEnum('dispatch.label_run.mode', ['production', 'shipment']);
/** + contractor_debit_note [06 §16 #12]. */
export const NOTE_KIND = defineEnum('dispatch.credit_debit_note.kind', [
  'supplier_debit_note',
  'customer_credit_memo',
  'contractor_debit_note',
]);
export const SKID_TYPE = defineEnum('dispatch.shipment_skid.type', ['regular', 'euro']);
/**
 * CR-BG2d (W2d.shipping R-SCH-SHP-1): `dispatch.shipment_scan` — a garment scanned into a carton (`pack`) or a carton
 * label scanned onto a skid (`load`); unknown / duplicate / foreign scans are stored as `refused` with their reason.
 */
export const SHIPMENT_SCAN_KIND = defineEnum('dispatch.shipment_scan.kind', ['pack', 'load']);
export const SHIPMENT_SCAN_OUTCOME = defineEnum('dispatch.shipment_scan.outcome', ['accepted', 'refused']);
export const SHIPMENT_SCAN_REFUSAL = defineEnum('dispatch.shipment_scan.refusal_reason', [
  'unknown',
  'duplicate',
  'not_on_shipment',
  'over_packed',
  'wrong_skid',
]);

// ---- planning ----
export const REQUIREMENT_DEMAND_BASIS = defineEnum('planning.requirement_run.demand_basis', [
  'confirmed',
  'confirmed_draft',
  'confirmed_forecast',
]);
export const REQUIREMENT_ITEM_TYPE = defineEnum('planning.requirement_run.item_type', [
  'fabric',
  'trim',
  'both',
]);
export const REQUIREMENT_NETTING = defineEnum('planning.requirement_run.netting', [
  'stock_and_po',
  'stock_only',
]);
export const REQUIREMENT_ACTION = defineEnum('planning.requirement_line.action', [
  'raise_po',
  'covered',
  'expedite',
  'none',
]);
export const REQUIREMENT_DEMAND_SOURCE = defineEnum('planning.requirement_demand.source_type', [
  'sales_order_line',
  'cutting_slip',
  'forecast',
  'programme_line',
]);

// ---- channels ----
export const D2C_FULFILMENT_STATUS = defineEnum('channels.d2c_fulfilment.status', [
  'pending',
  'packed',
  'shipped',
  'cancelled',
]);
export const D2C_REFUND_DISPOSITION = defineEnum('channels.d2c_refund_line.disposition', [
  'restock',
  'seconds',
  'scrap',
  'no_return',
]);
/**
 * CR-BG2f (W2f.d2c-orders R-D2CO-FU-1, BR-D2C-022): `held` = the payout's net does not tie to its transactions or a
 * charge / refund is unmatched; it is never bank-matched until resolved.
 */
export const D2C_PAYOUT_STATUS = defineEnum('channels.d2c_payout.status', [
  'scheduled',
  'paid',
  'reconciled',
  'held',
]);
export const D2C_PAYOUT_TXN_TYPE = defineEnum('channels.d2c_payout_txn.txn_type', [
  'charge',
  'refund',
  'adjustment',
  'fee',
  'chargeback',
]);
export const D2C_DAILY_SUMMARY_STATUS = defineEnum('channels.d2c_daily_summary.status', [
  'open',
  'posted',
  'exported',
]);
export const STOCK_SYNC_RESULT = defineEnum('channels.stock_sync_log.result', [
  'ok',
  'failed',
  'skipped',
]);
export const WEBHOOK_EVENT_STATUS = defineEnum('channels.webhook_event.status', [
  'received',
  'processed',
  'failed',
  'ignored',
]);
export const D2C_EXCEPTION_TYPE = defineEnum('channels.d2c_exception.type', [
  'unmapped_variant',
  'oversell',
  'tax_mismatch',
  'duplicate',
  'payment_pending',
  'address_invalid',
  'sync_failed',
]);
// CR-BG2f (W2f.d2c-sync R7, 07 §15.2 #6 / #9): publish jobs (diff → approve → run → result) and web allocation plans.
export const D2C_PUBLISH_JOB_STATUS = defineEnum('channels.d2c_publish_job.status', [
  'pending',
  'approved',
  'running',
  'published',
  'failed',
  'cancelled',
]);
export const D2C_PUBLISH_KIND = defineEnum('channels.d2c_publish_job.kind', [
  'create_product',
  'add_variants',
  'update_content',
  'update_price',
  'archive',
]);
export const D2C_ALLOCATION_PLAN_STATUS = defineEnum('channels.d2c_allocation_plan.status', [
  'draft',
  'active',
  'closed',
  'cancelled',
]);
/** Where a planned web quantity comes from: main-warehouse stock, or dedicated production via a D2C sales order. */
export const D2C_ALLOCATION_SOURCE = defineEnum('channels.d2c_allocation_plan_line.source', ['stock', 'production']);

// ---- finance ----
/** CR-BG2e (W2e.close R-GL-CLS-2, BR-CLS-010/011): `closing` = P12 closed and the closing journal posted, before sign-off. */
export const FISCAL_YEAR_STATUS = defineEnum('finance.fiscal_year.status', ['open', 'closing', 'closed']);
export const LOCK_MODULE = defineEnum('finance.period_lock.module', [
  'gl',
  'ap',
  'ar',
  'inventory',
  'bank',
  'costing',
]);
export const CLOSE_CHECK_RESULT = defineEnum('finance.period_close_check.result', [
  'pass',
  'fail',
  'warn',
]);
export const GL_ACCOUNT_TYPE = defineEnum('finance.gl_account.account_type', [
  'asset',
  'liability',
  'equity',
  'revenue',
  'expense',
]);
export const GL_STATEMENT_CLASS = defineEnum('finance.gl_account.statement_class', [
  'balance_sheet',
  'profit_loss',
]);
export const GL_NORMAL_BALANCE = defineEnum('finance.gl_account.normal_balance', [
  'debit',
  'credit',
]);
export const GL_CONTROL_KIND = defineEnum('finance.gl_account.control_kind', [
  'none',
  'ar',
  'ap',
  'inventory',
  'bank',
  'tax',
]);
export const TAX_APPLIES_TO = defineEnum('finance.tax_code.applies_to', [
  'purchase',
  'sale',
  'both',
]);
export const JOURNAL_SOURCE = defineEnum('finance.journal_entry.source_type', [
  'manual',
  'ap',
  'ar',
  'payment',
  'inventory',
  'production',
  'fx_reval',
  'd2c',
  'bank',
  'opening',
  'close',
  // CR-W2b-12 (CR-W2b-6 R-CORE-GL-3): BR-GL-011 P0 source for adjustments to a closed prior fiscal year.
  'prior_year_adjustment',
]);
/** CR-W2b-12 (CR-W2b-6 R-SCH-1): how a journal's rate was chosen (system rate, manual within / beyond tolerance). */
export const FX_RATE_KIND = defineEnum('finance.journal_entry.fx_rate_kind', [
  'system',
  'tolerance',
  'override',
]);
export const AP_DOC_TYPE = defineEnum('finance.ap_document.doc_type', [
  'invoice',
  'credit_memo',
  'debit_memo',
]);
export const AP_MATCH_STATUS = defineEnum('finance.ap_document.match_status', [
  'not_required',
  'awaiting_receipt',
  'matched',
  'qty_variance',
  'price_variance',
  'no_po',
]);
/** + `email` (CR-BG2c, W2c.ap R-SCH-AP-3: BR-AP-003 c AP inbox channel; the channel itself is deferred). */
export const AP_CAPTURE_SOURCE = defineEnum('finance.ap_document.capture_source', [
  'manual',
  'ocr',
  'migration',
  'email',
]);
/** CR-BG2c (W2c.ap R-SCH-AP-2): why an AP document is on hold (was a `[kind]` prefix in hold_reason). */
export const AP_HOLD_KIND = defineEnum('finance.ap_document.hold_kind', [
  'credit_note',
  'await_receipt',
  'other',
]);
export const AP_MATCH_RESULT = defineEnum('finance.ap_match_result.result', [
  'pass',
  'fail',
  'accepted',
]);
export const AR_DOC_TYPE = defineEnum('finance.ar_document.doc_type', [
  'invoice',
  'credit_note',
  'debit_note',
]);
/** CR-BG2c (W2c.ar-bank R-SCH-AR-1): BR-AR-003 reason of a zero-price invoice line (was the header reason code). */
export const AR_NO_CHARGE_REASON = defineEnum('finance.ar_document_line.no_charge_reason', [
  'sample',
  'no_charge',
]);
export const TAX_DOC_KIND = defineEnum('finance.document_tax.doc_kind', ['ap', 'ar']);
export const DEDUCTION_TYPE = defineEnum('finance.customer_deduction.deduction_type', [
  'markdown',
  'listing_allowance',
  'volume_rebate',
  'chargeback',
  'short_ship',
  'return',
  'freight',
  'other',
]);
export const PAYMENT_DIRECTION = defineEnum('finance.payment.direction', ['payment', 'receipt']);
/**
 * CR-BG2c (W2c.ap R-SCH-AP-4, W2c.ar-bank R-SCH-AR-1): `sage_mirror` = a payment made in Sage during the parallel run
 * and mirrored here (BR-AP-036; was `legacy_ref = 'sage:<batch>'`).
 */
export const PAYMENT_ORIGIN = defineEnum('finance.payment.origin', ['native', 'sage_mirror']);
export const FINANCE_PAYMENT_METHOD = defineEnum('finance.payment.method', [
  'wire_tt',
  'cheque',
  'eft',
  'card',
  'd2c_payout',
  'other',
]);
export const PAYMENT_RUN_TYPE = defineEnum('finance.payment_run.run_type', [
  'supplier',
  'contractor',
  'tt_wire',
  'cheque',
]);
export const SAGE_BATCH_TYPE = defineEnum('finance.sage_export_batch.batch_type', [
  'oe_invoice',
  'ar_invoice',
  'ar_credit',
  'ap_invoice',
  'ap_credit',
  'ic_adjustment',
  'ic_transfer',
  'gl_journal',
]);
export const RECONCILIATION_AREA = defineEnum('finance.daily_reconciliation.area', [
  'ar',
  'ap',
  'inventory',
  'gl',
  'd2c',
]);
export const RECONCILIATION_STATUS = defineEnum('finance.daily_reconciliation.status', [
  'open',
  'explained',
  'resolved',
]);
/** CR-BG2e (W2e.sage-bridge R-SCH-SB-2, BR-PAR-040/041): what a stored Sage upload holds. */
export const SAGE_UPLOAD_KIND = defineEnum('finance.sage_tb_upload.kind', ['trial_balance', 'aged_ar', 'aged_ap']);
/** CR-BG2e (W2e.close R-SCH-CLS-1, BR-CSH-002): how often a recurring cash-forecast item repeats. */
export const CASH_FORECAST_FREQUENCY = defineEnum('finance.cash_forecast_item.frequency', [
  'once',
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'annually',
]);
/** Ledger mode per period [07 §1.2; BUILD-PLAN §9 fiscal_period mode]. */
export const LEDGER_MODE = defineEnum('finance.fiscal_period.mode', ['parallel', 'live']);
